import { Provider } from '@angular/core';
import { FieldNameService } from '@shared/naming/field-name.service';
import { ModelNameService } from '@shared/naming/model-name.service';
import {
  DEFAULT_NAMING_KEY,
  FIELD_NAMES,
  MODEL_NAME_LOWER,
  MODEL_NAME_TITLE,
  NamingConfigInput,
  PluralMap,
} from '@shared/naming/naming.module';
import { ApiErrorService } from '@shared/services/api-messaging/api-error.service';
import { ApiMessagingService } from '@shared/services/api-messaging/api-messaging.service';
import { SnackbarService } from '@shared/services/snackbar.service';

function defaultLowerMap(titleMap: PluralMap): PluralMap {
  const lowerMap = { ...titleMap }; // Duplicate the object
  Object.keys(lowerMap).forEach(
    // Capital letters followed by lowercase letters are made lowercase.
    // E.g. "PAR" stays "PAR" but "Par" becomes "par"
    k => (lowerMap[k] = lowerMap[k].replace(/[A-Z](?=[a-z])/g, char => char.toLocaleLowerCase()))
  );
  return lowerMap;
}

export interface NamingConfig {
  readonly key: string;
  forChild(): Provider[];
  forRoot(): Provider[];
}

export function NamingConfig<TModel>({
  fieldNames,
  modelName,
  key,
}: NamingConfigInput<TModel>): NamingConfig {
  const [titleMap, givenLowerMap]: [PluralMap, PluralMap?] = Array.isArray(modelName)
    ? modelName
    : [modelName];
  const lowerMap = givenLowerMap ?? defaultLowerMap(titleMap);

  return {
    key: key,

    forChild() {
      return [
        { provide: DEFAULT_NAMING_KEY, useValue: key },
        FieldNameService,
        ModelNameService,
        SnackbarService,
        ApiMessagingService,
        ApiErrorService,
      ];
    },

    forRoot() {
      return [
        { provide: MODEL_NAME_TITLE, useValue: { [key]: titleMap }, multi: true },
        { provide: MODEL_NAME_LOWER, useValue: { [key]: lowerMap }, multi: true },
        { provide: FIELD_NAMES, useValue: { [key]: fieldNames }, multi: true },
      ];
    },
  };
}
